import * as iots from "io-ts";
import {
    type ActionNode,
    type BuilderAction,
    type BuilderActionPerApp,
    type SkeletonActionNode,
    isSkeletonActionNode,
    createActionNodeMap,
    stripActionNode,
    skeletonizeAction,
    mutateBuilderActionWithSkeleton,
    replaceNodeByKey,
    diffActionNodes,
} from "@glide/app-description";
import { deepEqual } from "@glide/support";
import { assert, assertNever, definedMap } from "@glideapps/ts-necessities";
import { ErrorInfo, isErrorInfo, type ErrorOr } from "@glide/error-info";

export enum BuilderActionMutationKind {
    // This mutation currently only exists to make the optimistic mutation
    // manager also able to create builder actions, vs just updating them.
    CreateBuilderAction = "CREATE_BUILDER_ACTION",
    UpdateBuilderActionAction = "UPDATE_BUILDER_ACTION_ACTION",
    UpdateBuilderActionPerApp = "UPDATE_BUILDER_ACTION_PER_APP",
    UpdateBuilderActionTitleAndDescription = "UPDATE_BUILDER_ACTION_TITLE_AND_DESCRIPTION",
    UpdateBuilderActionNode = "UPDATE_BUILDER_ACTION_NODE",
}

const actionNodeCodec = iots.record(iots.string, iots.unknown) as unknown as iots.Type<
    Partial<ActionNode> & { key: string },
    unknown,
    unknown
>;
const perAppCodec = iots.record(iots.string, iots.unknown) as unknown as iots.Type<
    BuilderActionPerApp,
    unknown,
    unknown
>;

export const createBuilderActionMetaMutationCodec = iots.type({
    kind: iots.literal(BuilderActionMutationKind.CreateBuilderAction),
    data: iots.type({
        actionID: iots.string,
        value: iots.intersection([
            iots.type({
                action: iots.unknown as iots.Type<BuilderAction["action"], unknown, unknown>,
                perApp: iots.record(iots.string, perAppCodec),
            }),
            iots.partial({
                title: iots.string,
                description: iots.string,
            }),
        ]),
    }),
});

// Custom io-ts type for SkeletonActionNode using the validator
const skeletonActionNodeCodec = new iots.Type<SkeletonActionNode>(
    "SkeletonActionNode",
    isSkeletonActionNode,
    (u, c) => {
        if (isSkeletonActionNode(u)) {
            return iots.success(u);
        } else {
            return iots.failure(u, c, "Value is not a valid SkeletonActionNode");
        }
    },
    iots.identity
);

export const updateBuilderActionActionMetaMutationCodec = iots.type({
    kind: iots.literal(BuilderActionMutationKind.UpdateBuilderActionAction),
    data: iots.intersection([
        iots.type({
            actionID: iots.string,
            updates: iots.type({
                action: skeletonActionNodeCodec,
            }),
        }),
        iots.partial({
            before: iots.type({
                skeleton: skeletonActionNodeCodec,
                deletedNodes: iots.readonlyArray(actionNodeCodec),
            }),
        }),
    ]),
});

export const updateBuilderActionNodeMetaMutationCodec = iots.type({
    kind: iots.literal(BuilderActionMutationKind.UpdateBuilderActionNode),
    data: iots.intersection([
        iots.type({
            actionID: iots.string,
            updates: actionNodeCodec,
        }),
        iots.partial({
            before: actionNodeCodec,
        }),
    ]),
});

export const updateBuilderActionPerAppMetaMutationCodec = iots.type({
    kind: iots.literal(BuilderActionMutationKind.UpdateBuilderActionPerApp),
    data: iots.intersection([
        iots.type({
            actionID: iots.string,
            updates: iots.type({
                perApp: iots.record(iots.string, perAppCodec),
            }),
        }),
        iots.partial({
            before: iots.type({
                perApp: iots.record(iots.string, perAppCodec),
            }),
        }),
    ]),
});

export const updateBuilderActionTitleAndDescriptionMetaMutationCodec = iots.type({
    kind: iots.literal(BuilderActionMutationKind.UpdateBuilderActionTitleAndDescription),
    data: iots.intersection([
        iots.type({
            actionID: iots.string,
            updates: iots.partial({
                title: iots.string,
                description: iots.string,
            }),
        }),
        iots.partial({
            before: iots.partial({
                title: iots.string,
                description: iots.string,
            }),
        }),
    ]),
});

export type BuilderActionMutation = iots.TypeOf<
    | typeof createBuilderActionMetaMutationCodec
    | typeof updateBuilderActionActionMetaMutationCodec
    | typeof updateBuilderActionPerAppMetaMutationCodec
    | typeof updateBuilderActionTitleAndDescriptionMetaMutationCodec
    | typeof updateBuilderActionNodeMetaMutationCodec
>;

/**
 * Returns an `ErrorInfo` if the mutation is invalid in the current state, or
 * is not allowed by its condition.  Returns `undefined` if the mutation is
 * a no-op.
 */
export function applyBuilderActionMutationFull(
    builderAction: BuilderAction,
    { kind, data }: BuilderActionMutation,
    respectCondition: boolean
): ErrorOr<{ newState: BuilderAction; antiMutation: BuilderActionMutation | undefined }> | undefined {
    switch (kind) {
        case BuilderActionMutationKind.CreateBuilderAction:
            // This is a bit weird
            return { newState: data.value, antiMutation: undefined };

        case BuilderActionMutationKind.UpdateBuilderActionAction: {
            const { updates, before } = data;
            const { action } = updates;

            // Check for no-op: if the new skeleton equals the current skeleton
            const currentSkeleton = skeletonizeAction(builderAction.action);
            if (deepEqual(currentSkeleton, action)) {
                return undefined;
            }

            if (respectCondition && before !== undefined) {
                const map = createActionNodeMap(builderAction.action);
                for (const deletedNode of before.deletedNodes) {
                    const existingNode = map.get(deletedNode.key);
                    if (existingNode === undefined) {
                        // The mutation is trying to delete a node that
                        // doesn't exist (anymore), which is fine.
                        continue;
                    }
                    if (!deepEqual(stripActionNode(existingNode), deletedNode)) {
                        return new ErrorInfo(412, "Step cannot be deleted because it has changed");
                    }
                }
                if (!deepEqual(currentSkeleton, before.skeleton)) {
                    return new ErrorInfo(412, "Workflow cannot be updated because it has changed");
                }
            }

            const newAction = mutateBuilderActionWithSkeleton(builderAction.action, action);

            const diff = diffActionNodes(newAction, builderAction.action);

            return {
                newState: {
                    ...builderAction,
                    action: newAction,
                },
                antiMutation: definedMap(diff.skeleton, s => ({
                    kind: BuilderActionMutationKind.UpdateBuilderActionAction,
                    data: {
                        actionID: data.actionID,
                        updates: {
                            action: s,
                        },
                        before: {
                            skeleton: diff.skeletonBefore,
                            deletedNodes: diff.deletedNodes,
                        },
                    },
                })),
            };
        }

        case BuilderActionMutationKind.UpdateBuilderActionPerApp: {
            const { updates, before } = data;
            const { perApp } = updates;

            // Check for no-op: if the new perApp equals the current perApp
            if (deepEqual(builderAction.perApp, perApp)) {
                return undefined;
            }

            if (respectCondition && before !== undefined) {
                if (!deepEqual(builderAction.perApp, before.perApp)) {
                    return new ErrorInfo(412, "Action cannot be updated because it has changed");
                }
            }

            return {
                newState: {
                    ...builderAction,
                    perApp,
                },
                antiMutation: {
                    kind: BuilderActionMutationKind.UpdateBuilderActionPerApp,
                    data: {
                        actionID: data.actionID,
                        updates: { perApp: builderAction.perApp },
                        before: updates,
                    },
                },
            };
        }

        case BuilderActionMutationKind.UpdateBuilderActionNode: {
            const { updates, before } = data;
            const { key } = updates;

            const map = createActionNodeMap(builderAction.action);
            const existingNode = definedMap(map.get(key), stripActionNode);

            // Check for no-op: if the updates match the existing node
            if (existingNode !== undefined && deepEqual(existingNode, updates)) {
                return undefined;
            }

            if (respectCondition && before !== undefined) {
                if (existingNode === undefined) {
                    return new ErrorInfo(412, "Step cannot be updated because it doesn't exist");
                }
                if (!deepEqual(existingNode, before)) {
                    return new ErrorInfo(412, "Step cannot be updated because it has changed");
                }
            }

            return {
                newState: {
                    ...builderAction,
                    action: replaceNodeByKey(builderAction.action, key, updates),
                },
                antiMutation: definedMap(existingNode, e => ({
                    kind: BuilderActionMutationKind.UpdateBuilderActionNode,
                    data: {
                        actionID: data.actionID,
                        updates: e,
                        before: updates,
                    },
                })),
            };
        }

        case BuilderActionMutationKind.UpdateBuilderActionTitleAndDescription: {
            const { updates, before } = data;
            const { title, description } = updates;
            if (title === undefined && description === undefined) return undefined;

            // Check for no-op: if the new values equal the current values
            const titleChanged = title !== undefined && title !== builderAction.title;
            const descriptionChanged = description !== undefined && description !== builderAction.description;
            if (!titleChanged && !descriptionChanged) {
                return undefined;
            }

            if (respectCondition && before !== undefined) {
                if (before.title !== undefined && before.title !== builderAction.title) {
                    return new ErrorInfo(412, "Title cannot be updated because it has changed");
                }
                if (before.description !== undefined && before.description !== builderAction.description) {
                    return new ErrorInfo(412, "Description cannot be updated because it has changed");
                }
            }

            return {
                newState: {
                    ...builderAction,
                    title: title ?? builderAction.title,
                    description: description ?? builderAction.description,
                },
                antiMutation: {
                    kind: BuilderActionMutationKind.UpdateBuilderActionTitleAndDescription,
                    data: {
                        actionID: data.actionID,
                        updates: {
                            title: updates.title !== undefined ? builderAction.title : undefined,
                            description: updates.description !== undefined ? builderAction.description : undefined,
                        },
                        before: updates,
                    },
                },
            };
        }

        default:
            return assertNever(kind);
    }
}

export function applyBuilderActionMutation(
    builderAction: BuilderAction,
    mutation: BuilderActionMutation
): BuilderAction {
    const result = applyBuilderActionMutationFull(builderAction, mutation, false);
    assert(!isErrorInfo(result));
    return result?.newState ?? builderAction;
}
